// Részleg-lista panel (org-structure „Részlegek" fül) — a KÖZÖS DataTable-re épül (a service-catalog/employees
// referencia-minta). A szervezeti fa LEGFELSŐ szintje: a részleg CSAPATOKAT tart, a beosztások egy szinttel
// lejjebb, a csapatok alatt élnek. Szerver-oldali (BFF) szabad-szöveg kereső (300 ms debounce, név/slug — és
// a CSAPATAI nevére is illeszt, mert a felhasználó gyakran a csapatot ismeri), szerver-rendezés (name | slug),
// végtelen görgetés (100/lap), darabszám-meta a toolbarban. A /org-structure/departments BFF-végpontról
// fogyaszt (workforce.departments busz-erőforrás, i18n név/leírás hu/en-re bontva).
//
// A RÉSZLEGNEK NINCS SZÍNE (a szín a csapat szintjén él) — ezért nincs `color` rendezés-kulcs sem, és a sor a
// CSAPATAI színes badge-eit mutatja: a lap így egy pillantással megmutatja a részleg szín-palettáját.
//
// Page-gate: `organizational_structure.view`. CRUD sor-akciók: „+ Új" + ceruza (organizational_structure.manage
// → DepartmentFormSheet) + kuka (organizational_structure.delete → ConfirmDialog + optimista eltávolítás).
// Törlés-guard: a szerver 409-et ad, ha a részleghez CSAPAT tartozik → a conflict-ág visszaállítja a sort és a
// „használatban" toastot mutatja.
import { type OrgDepartmentRowSchema } from '@atlas/contracts';
import { PlusIcon } from '@heroicons/react/24/outline';
import { type InfiniteData, useInfiniteQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type z } from 'zod';

import { DepartmentFormSheet } from '@/components/org-structure/department-form-sheet';
import { OrgStructureTabs, type OrgTab } from '@/components/org-structure/org-structure-tabs';
import { AppShell } from '@/components/shared/app-shell';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { EmptyState } from '@/components/shared/empty-state';
import { ExpandableCell } from '@/components/shared/expandable-cell';
import { ListErrorState } from '@/components/shared/list-error-state';
import { ListNewButton } from '@/components/shared/list-new-button';
import { DeleteRowAction, EditRowAction, RowActions } from '@/components/shared/list-row-actions';
import { ListToolbar } from '@/components/shared/list-toolbar';
import { TeamBadge } from '@/components/shared/team-badge';
import { Avatar } from '@/components/ui/avatar';
import { Text } from '@/components/ui/text';
import { departmentKeys, positionKeys, teamKeys } from '@/features/org-structure/api';
import { useDeepLinkSheet } from '@/hooks/use-deep-link-sheet';
import { api } from '@/lib/api';
import { formatName } from '@/lib/format-name';
import { useEntityMutation } from '@/lib/use-entity-mutation';
import { usePermissions } from '@/lib/use-permissions';
import { isForbiddenStatus } from '@/lib/error-status';

type DepartmentRow = z.infer<typeof OrgDepartmentRowSchema>;
type Member = DepartmentRow['members'][number];
type ListPage = Awaited<ReturnType<typeof api.orgDepartments.query>>;

const PAGE_SIZE = 100;

export function DepartmentsPanel({
  tab,
  onTabChange,
  deepLinkEditId,
  onClearDeepLink,
}: {
  tab: OrgTab;
  onTabChange: (value: string) => void;
  /** Deep-link: a `?edit=` paraméterből érkező részleg-id (a szerkesztő-sheet erre nyílik, ha a sor betöltött). */
  deepLinkEditId?: string | undefined;
  /** A deep-link param törlése (a sheet záráskor hívjuk). */
  onClearDeepLink?: () => void;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language.toLowerCase().startsWith('en');
  const { can, isLoading: permsLoading } = usePermissions();
  const canView = can('organizational_structure.view');
  const canManage = can('organizational_structure.manage');
  const canDelete = can('organizational_structure.delete');

  // Szerkesztő-sheet: `null` → zárt; `{ department: null }` → LÉTREHOZÁS; `{ department }` → SZERKESZTÉS.
  const [formTarget, setFormTarget] = useState<{ department: DepartmentRow | null } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DepartmentRow | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<string>('name');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    const timer = setTimeout(() => setQ(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const gated = !permsLoading && !canView;
  const filters = { q, sort, order };
  const listKey = departmentKeys.list(filters);

  const infinite = useInfiniteQuery({
    queryKey: listKey,
    enabled: !gated,
    queryFn: ({ pageParam }) =>
      api.orgDepartments.query({
        query: { q: q || undefined, sort, order, limit: PAGE_SIZE, offset: pageParam },
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.status === 200 ? (lastPage.body.nextOffset ?? undefined) : undefined,
  });

  const pages = useMemo(() => infinite.data?.pages ?? [], [infinite.data]);
  const rows = useMemo(
    () => pages.flatMap((p) => (p.status === 200 ? p.body.departments : [])),
    [pages],
  );

  // Deep-link: a `?edit=<id>` részlegre nyitjuk a szerkesztő-sheetet, amint a sor betöltött (különben graceful no-op).
  useDeepLinkSheet<DepartmentRow>({
    id: deepLinkEditId,
    rows,
    getId: (d) => d.id,
    onOpen: (d) => setFormTarget({ department: d }),
  });

  const okPage = pages.find((p) => p.status === 200);
  const total = okPage?.status === 200 ? okPage.body.total : 0;
  const hasNoSuccess = pages.length > 0 && !pages.some((p) => p.status === 200);
  const isError = gated || infinite.isError || hasNoSuccess;
  const isForbidden = gated || pages.some((p) => isForbiddenStatus(p.status));
  const hasFilters = q !== '';

  const toggleSort = useCallback((col: string) => {
    setSort((prev) => {
      if (prev === col) {
        setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setOrder('asc');
      return col;
    });
  }, []);
  const { fetchNextPage } = infinite;
  const loadMore = useCallback(() => void fetchNextPage(), [fetchNextPage]);

  // Törlés (optimista sor-eltávolítás + rollback). A REFERENCIA-GUARD 409-et ad, ha van hozzá beosztás — ilyenkor
  // a conflict-ág visszaállítja a listát (rollback) és a „használatban" toastot mutatja (nincs végleges törlés).
  const removeMutation = useEntityMutation<{ id: string; version: number }, InfiniteData<ListPage>>(
    {
      mutationFn: ({ id, version }) =>
        api.departmentDelete.mutation({
          params: { id },
          headers: { 'if-match': String(version) },
        }),
      invalidateKeys: [
        departmentKeys.lists(),
        departmentKeys.refs(),
        teamKeys.lists(),
        positionKeys.lists(),
      ],
      optimistic: {
        queryKey: listKey,
        update: (old) => {
          if (!old) return old;
          const id = deleteTarget?.id;
          if (!id) return old;
          return {
            ...old,
            pages: old.pages.map((p) =>
              p.status === 200
                ? {
                    ...p,
                    body: {
                      ...p.body,
                      departments: p.body.departments.filter((d) => d.id !== id),
                      total: Math.max(0, p.body.total - 1),
                    },
                  }
                : p,
            ),
          };
        },
      },
      messages: {
        success: t('toast:org_structure.departments.deleted'),
        // A 409 itt a referencia-guard (részleg használatban) — a rollback után ezt mutatjuk.
        conflict: t('toast:org_structure.departments.inUse'),
        forbidden: t('toast:org_structure.departments.forbidden'),
        validation: t('toast:org_structure.departments.validation'),
        error: t('toast:org_structure.departments.error'),
      },
    },
  );

  function confirmDelete(): void {
    if (!deleteTarget) return;
    removeMutation.mutate(
      { id: deleteTarget.id, version: deleteTarget.version },
      { onSettled: () => setDeleteTarget(null) },
    );
  }

  const emDash = <span className="text-muted-foreground">—</span>;

  // Egy tag megjelenítendő neve (locale-sorrend, mint a support-teams/munkatársak-oldal).
  const memberName = useCallback(
    (m: Member): string =>
      formatName(
        { givenName: m.givenName, familyName: m.familyName, displayName: m.displayName },
        i18n.language,
      ),
    [i18n.language],
  );

  const columns: DataTableColumn<DepartmentRow>[] = [
    {
      // 1. Részleg — a lokalizált név SZÖVEGGEL (rendszerszinten az 1. oszlop mindig a fő-név, NEM badge).
      // Színe SZÁNDÉKOSAN nincs: a szervezeti szín a csapaté (lásd a 2. oszlopot).
      // A slug NEM látszik a listán (a szerkesztő-modalban igen).
      id: 'name',
      header: t('orgStructure.departments.columns.name'),
      sortable: true,
      cardLabel: t('orgStructure.departments.columns.name'),
      width: 'minmax(0,2fr)',
      multiline: true,
      cell: (d) => {
        const name = isEn ? d.nameEn : d.nameHu;
        // A LEÍRÁS halvány másodlagos sorban a név alatt (a legacy önálló „Leírás" oszlopa helyett): mérve
        // mind a 11 részlegnek VAN leírása, tehát valódi tartalom — de külön oszlopként kiszorítaná a
        // beosztás-/munkatárs-cellát. (A beosztásokon a legacy leírás-oszlopa ÜRES volt: 0/34 → nem hozzuk.)
        const description = (isEn ? d.descriptionEn : d.descriptionHu) ?? '';
        return (
          <div className="min-w-0 space-y-0.5">
            <Text variant="itemTitle" as="div" className="truncate">
              {name || d.slug || emDash}
            </Text>
            {description !== '' && (
              <Text tone="tertiary" as="div" className="truncate">
                {description}
              </Text>
            )}
          </div>
        );
      },
    },
    {
      // 2. Csapatok — a részlegHEZ TARTOZÓ csapatok, a SAJÁT színükkel. EZ a lap tárgya: melyik részlegben
      // milyen csapatok vannak; a beosztások egy szinttel lejjebb, a Csapatok fülön láthatók, a munkatárs-
      // oszlop (lásd lent) pedig a „kik" kérdésre válaszol.
      id: 'teams',
      header: t('orgStructure.departments.columns.teams'),
      cardLabel: t('orgStructure.departments.columns.teams'),
      width: 'minmax(0,2.5fr)',
      multiline: true,
      // Mobil-kártya: REJTVE — a kártyán a munkatársak viszik a subtitle-t (egy kibontó/kártya a szabály).
      card: 'hidden',
      cell: (d) =>
        d.teams.length === 0 ? (
          emDash
        ) : (
          <ExpandableCell<{ id: string; name: string; color: string }>
            items={d.teams}
            getKey={(tm) => tm.id}
            visible={2}
            threshold={3}
            moreLabel={(n) => t('orgStructure.departments.moreTeams', { count: n })}
            triggerAriaLabel={t('orgStructure.departments.moreTeamsAria')}
            renderItem={(tm) => (
              <TeamBadge name={tm.name} color={tm.color} className="max-w-full truncate" />
            )}
          />
        ),
      exportValue: (d) => d.teams.map((tm) => tm.name).join(', '),
    },
    {
      // 3. Munkatársak — a részleghez tartozó belső munkatársak (a CSAPATAIKON és azok beosztásain át, két
      // szinten aggregálva): profilkép + név;
      // 3+ tagnál legördülő (ExpandableCell, a support-teams „Tagok" oszlop pontos designja).
      id: 'members',
      header: t('orgStructure.columns.employees'),
      cardLabel: t('orgStructure.columns.employees'),
      card: 'subtitle',
      width: 'minmax(0,3fr)',
      multiline: true,
      cell: (d) =>
        d.members.length === 0 ? (
          emDash
        ) : (
          <ExpandableCell<Member>
            items={d.members}
            getKey={(m) => m.id}
            visible={2}
            threshold={3}
            moreLabel={(n) => t('orgStructure.moreMembers', { count: n })}
            triggerAriaLabel={t('orgStructure.moreMembersAria')}
            renderItem={(m) => {
              const name = memberName(m);
              return (
                <span className="flex min-w-0 items-center gap-1.5">
                  <Avatar name={name} src={m.avatarUrl} size="sm" accentColor={m.teamColor} />
                  <span className="truncate">{name}</span>
                </span>
              );
            }}
          />
        ),
    },
  ];

  return (
    <AppShell
      // Mobilon a lebegő FAB (primaryAction) az AKTÍV fül (Részlegek) create-jét nyitja — csak manage-joggal.
      // Desktopon a toolbar ListNewButton-ja (lásd lent, `hidden md:block`).
      primaryAction={
        canManage
          ? {
              icon: <PlusIcon className="size-5 stroke-2" />,
              label: t('orgStructure.departments.new'),
              onClick: () => setFormTarget({ department: null }),
            }
          : undefined
      }
      toolbar={
        <div>
          <div className="px-4 pt-2">
            <OrgStructureTabs value={tab} onValueChange={onTabChange} />
          </div>
          <ListToolbar
            search={{
              value: searchInput,
              onChange: setSearchInput,
              placeholder: t('common.searchPlaceholder'),
            }}
            meta={
              infinite.isFetching && !infinite.isFetchingNextPage
                ? t('orgStructure.loading')
                : !isError && !infinite.isLoading
                  ? t('orgStructure.departments.countOf', { shown: rows.length, total })
                  : ''
            }
            actions={
              // A New gomb CSAK ≥md — mobilon a lebegő FAB (primaryAction) kezeli a létrehozást.
              <div className="hidden md:block">
                <ListNewButton
                  label={t('orgStructure.departments.new')}
                  onClick={() => setFormTarget({ department: null })}
                  enabled={canManage}
                  disabledTooltip={t('common.noCreatePermission')}
                />
              </div>
            }
          />
        </div>
      }
    >
      <div className="flex h-full flex-col px-4 pb-2">
        <DataTable<DepartmentRow>
          columns={columns}
          rows={rows}
          getRowId={(d) => d.id}
          sort={sort}
          order={order}
          onSort={toggleSort}
          isLoading={!gated && (permsLoading || infinite.isLoading)}
          isError={isError}
          errorState={
            <ListErrorState forbidden={isForbidden} onRetry={() => void infinite.refetch()} />
          }
          emptyState={
            hasFilters ? (
              <EmptyState
                title={t('orgStructure.departments.noMatches')}
                description={t('orgStructure.departments.noMatchesDesc')}
              />
            ) : (
              <EmptyState
                title={t('orgStructure.departments.empty')}
                description={t('orgStructure.departments.emptyDesc')}
              />
            )
          }
          hasNextPage={infinite.hasNextPage}
          isFetchingNextPage={infinite.isFetchingNextPage}
          onLoadMore={loadMore}
          rowActionsLabel={t('orgStructure.columns.actions')}
          actionsColWidth={canManage && canDelete ? '7rem' : canManage || canDelete ? '4rem' : '0'}
          rowActions={
            canManage || canDelete
              ? (d) => (
                  <RowActions>
                    {canManage && (
                      <EditRowAction
                        label={t('orgStructure.edit')}
                        onClick={() => setFormTarget({ department: d })}
                      />
                    )}
                    {canDelete && (
                      <DeleteRowAction
                        label={t('orgStructure.delete')}
                        onClick={() => setDeleteTarget(d)}
                      />
                    )}
                  </RowActions>
                )
              : undefined
          }
        />
      </div>

      <DepartmentFormSheet
        open={formTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setFormTarget(null);
            onClearDeepLink?.(); // deep-link: záráskor a `?edit` param törlése (back-barát + újra-nyitható)
          }
        }}
        department={formTarget?.department ?? null}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={t('orgStructure.departments.deleteTitle')}
        description={t('orgStructure.departments.deleteDesc', {
          name: deleteTarget ? (isEn ? deleteTarget.nameEn : deleteTarget.nameHu) : '',
        })}
        confirmLabel={t('orgStructure.delete')}
        cancelLabel={t('orgStructure.cancel')}
        pendingLabel={t('orgStructure.deleting')}
        tone="danger"
        pending={removeMutation.isPending}
        onConfirm={confirmDelete}
      />
    </AppShell>
  );
}
